import React, { LegacyRef } from 'react'

// components
import { LoaderSpinner } from '@/components/ui/LoaderSpinner.component'
import Image from 'next/image'
import { IMAGE_PATHS_V2 } from '@/shared/consts/imagesPaths'

// styles
import {
  inputContainer,
  labelInput,
  inputContainerField,
  inputField,
  inputFieldError,
  loaderContainer,
  errorMessage,
  inputFieldPlaceholder,
  inputFieldPlaceholderActive,
  inputFieldWithPlaceHolderActive,
  validMessageStyle,
  inputLocationIcon,
  inputLocationField,
  locationPlaceholderActive,
  inputLocationPlaceholder,
} from '../styles'
import { cssClass } from '@/shared/utils/styles'
import { validateNumber } from '@/shared/utils/validators'
import { DEAULT_SIZES, SizeInput } from './interfaces'

interface Props {
  label?: string
  size?: SizeInput
  placeholder?: string
  value?: string | number
  maxLength?: number
  type?: string
  name?: string
  error?: string
  disabled?: boolean
  required?: boolean
  loading?: boolean
  showError?: boolean
  containerClassName?: string[]
  containerInputClassName?: string[]
  className?: string[]
  inputRef?: LegacyRef<HTMLInputElement>
  valid?: boolean
  validMessage?: string
  preventDefaultValue?: boolean
  iconLeft?: string | JSX.Element
  usePlaceholder?: boolean
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void
}

export const InputComponent: React.FC<Props> = ({
  label,
  placeholder,
  value = '',
  onChange,
  maxLength,
  type = 'text',
  name,
  error,
  disabled = false,
  required = false,
  showError = false,
  containerClassName = [],
  containerInputClassName = [],
  className = [],
  onFocus,
  onBlur,
  onKeyUp,
  inputRef,
  onPaste,
  onClick,
  preventDefaultValue = false,
  loading = false,
  valid = false,
  validMessage = '',
  usePlaceholder = false,
  iconLeft,
  size = 'small',
}) => {
  const [isFocused, setIsFocused] = React.useState(false)

  const changeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value
    const valueLength = e.target.value.length - 1
    const previewsChar = e.target.value[e.target.value.length - 2]
    const lastChar = e.target.value[e.target.value.length - 1]
    const isNumber = validateNumber(lastChar)

    if (type === 'number') {
      if (valueLength === 1 && previewsChar === '0') {
        newValue = lastChar
      }

      if (!isNumber) {
        newValue = newValue.slice(0, -1)
      }

      if (newValue === '' && !preventDefaultValue) {
        newValue = '0'
      }

      newValue = newValue.replace(/[a-z]/gi, '')
    }

    onChange &&
      onChange({
        ...e,
        target: { ...e.target, value: newValue, name: e.target.name },
      })
  }

  const hasValue = value !== '' && value !== undefined
  const placeholderStyle =
    hasValue || isFocused
      ? iconLeft
        ? locationPlaceholderActive
        : inputFieldPlaceholderActive
      : iconLeft
      ? inputLocationPlaceholder
      : inputFieldPlaceholder

  const inputFieldWithPlaceHolder =
    hasValue && placeholder ? inputFieldWithPlaceHolderActive : ''

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    onFocus && onFocus(e)
  }

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    onBlur && onBlur(e)
  }

  const paddingY = !usePlaceholder
    ? DEAULT_SIZES[size].withoutPlaceholder
    : DEAULT_SIZES[size].withPlaceholder
  const paddingL = iconLeft
    ? DEAULT_SIZES[size].withIconLeft
    : DEAULT_SIZES[size].withoutIconLeft
  const paddingR = iconLeft
    ? DEAULT_SIZES[size].withIconRight
    : DEAULT_SIZES[size].withoutIconRight

  return (
    <div
      onClick={onClick}
      className={cssClass([inputContainer, ...containerClassName])}
    >
      {label ? (
        <label className={labelInput} htmlFor={name}>
          {label} {required && <span className="text-info">*</span>}
        </label>
      ) : null}
      <div
        className={cssClass([inputContainerField, ...containerInputClassName])}
      >
        {iconLeft && (
          <div className={cssClass([inputLocationIcon, 'z-20'])}>
            {typeof iconLeft === 'string' ? (
              <Image src={iconLeft} alt={iconLeft} width={20} height={20} />
            ) : (
              iconLeft
            )}
          </div>
        )}
        {placeholder && placeholder !== undefined && !usePlaceholder ? (
          <span className={placeholderStyle}>{placeholder}</span>
        ) : null}

        <input
          onPaste={onPaste}
          ref={inputRef}
          type={type === 'number' ? 'text' : type}
          name={name}
          value={value}
          onChange={changeValue}
          disabled={disabled || loading}
          required={required}
          className={cssClass([
            iconLeft ? inputLocationField : inputField,
            inputFieldWithPlaceHolder,
            error || showError ? inputFieldError : '',
            paddingY,
            paddingL,
            paddingR,
            ...className,
          ])}
          max={maxLength}
          maxLength={maxLength}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyUp={onKeyUp}
          inputMode={type === 'number' ? 'numeric' : 'text'}
          placeholder={placeholder && usePlaceholder ? placeholder : ''}
        />
        {loading ? (
          <div className={loaderContainer}>
            <LoaderSpinner size={15} background="rgba(255, 255, 255, 0.4)" />
          </div>
        ) : null}

        {valid ? (
          <div className={loaderContainer}>
            <Image
              src={IMAGE_PATHS_V2.icons.commons + 'check-green.svg'}
              width={15}
              height={15}
              alt="check"
            />
          </div>
        ) : null}
      </div>
      {error && <span className={errorMessage}>{error}</span>}
      {valid && validMessage && !error && (
        <span className={validMessageStyle}>{validMessage}</span>
      )}
    </div>
  )
}
